import { Link } from '@tanstack/react-router';
import { Inbox } from 'lucide-react';

import { CATEGORY_META, partitionByGroup } from './practiceMeta';
import { PracticeGroupCard } from './PracticeGroupCard';
import { PracticeRow } from './PracticeRow';

import { usePractice } from '@/lib/hooks/useLms';
import type { PracticeCategoryKey } from '@/lib/api/lms';

const CARD = 'rounded-[20px] border border-[#ececec] bg-white shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)]';
const KNOWN: PracticeCategoryKey[] = ['assignments', 'problems', 'quizzes', 'projects', 'viva'];

function isCategoryKey(v: string): v is PracticeCategoryKey {
  return (KNOWN as string[]).includes(v);
}

export function PracticeCategoryList({ batchId, category }: { batchId: string; category: string }) {
  const { data, isLoading, isError, refetch } = usePractice(batchId);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#8b5cf6] border-t-transparent" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-[1150px] p-4 sm:p-6">
        <div className={`${CARD} p-6 text-center`}>
          <p className="text-[#9ca3af]">Couldn&apos;t load practice for this batch.</p>
          <button onClick={() => refetch()} className="mt-3 rounded-[14px] bg-[#8b5cf6] px-4 py-2 text-sm font-semibold text-white">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isPending = category === 'pending';
  const meta = isCategoryKey(category) ? CATEGORY_META[category] : null;
  const title = isPending ? 'Pending Work' : meta?.label ?? 'Practice';
  const items = isPending ? data.pending : data.items.filter((i) => i.category === category);
  // Authored sets collapse into one card each; everything else keeps the flat
  // list it has always had. `loose` is the whole list when nothing is grouped.
  const { grouped, loose } = partitionByGroup(items, data.groups ?? [], data.items);

  const Icon = meta?.icon ?? Inbox;
  const iconBg = meta?.bg ?? '#f5f3ff';
  const iconFg = meta?.fg ?? '#8b5cf6';

  return (
    <div className="mx-auto w-full max-w-[1150px] space-y-6 px-4 py-6 sm:px-10 sm:py-8">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] font-medium text-[#9ca3af]" aria-label="Breadcrumb">
        <Link to="/courses" className="transition-colors hover:text-[#ff5a1f]">My Batches</Link>
        <span aria-hidden>/</span>
        <Link to="/courses/$batchId" params={{ batchId }} className="text-[#ff5a1f] transition-colors hover:opacity-80">{data.batch_name}</Link>
        <span aria-hidden>/</span>
        <Link to="/courses/$batchId/practice" params={{ batchId }} className="transition-colors hover:text-[#ff5a1f]">Practice</Link>
        <span aria-hidden>/</span>
        <span className="font-semibold text-[#111827]">{title}</span>
      </nav>

      {/* Header */}
      <header className="flex items-center gap-4">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-[16px]" style={{ background: iconBg }}>
          <Icon className="size-6" style={{ color: iconFg }} />
        </span>
        <div>
          <h1 className="text-[26px] font-bold leading-tight tracking-tight text-[#111827] sm:text-[30px]">{title}</h1>
          <p className="text-[13px] text-[#9ca3af]">
            {grouped.length > 0 && `${grouped.length} ${grouped.length === 1 ? 'set' : 'sets'} · `}
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </p>
        </div>
      </header>

      {/* List */}
      {items.length > 0 ? (
        <div className="space-y-4">
          {grouped.map((g) => (
            <PracticeGroupCard
              key={g.group.key}
              grouped={g}
              batchId={batchId}
              // A lone set is the whole page; opening it saves a pointless tap.
              defaultOpen={grouped.length === 1 && loose.length === 0}
            />
          ))}
          {loose.length > 0 && (
            <div className={`${CARD} divide-y divide-[#f3f4f6] overflow-hidden`}>
              {loose.map((item) => (
                <PracticeRow key={item.id} item={item} batchId={batchId} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className={`${CARD} p-10 text-center text-[14px] text-[#9ca3af]`}>
          Nothing here yet.
        </div>
      )}
    </div>
  );
}
